/**
 * TwoFactorLockoutMixin
 *
 * Shared mixin for 2FA lockout and rate-limiting logic.
 * Used by: twofactor-login.vue, twofactor-settings.vue, twofactor-disable.vue, twofactor-switch.vue
 *
 * This mixin provides:
 * - State management for lockout and rate limiting
 * - Countdown timer management
 * - OTP verification attempt tracking
 * - Common computed properties (otpComplete, confirmationCode, attemptsClass)
 * - Lifecycle management for intervals
 */

import { Vue, Component } from 'vue-property-decorator'
import {
    TwoFactorStorage,
    TWOFACTOR_RATE_LIMITS,
    createEmptyOtpDigits,
    checkLockoutState,
    TwoFactorAuthStatusCommand
} from '@/app/auth/services/twofactor-storage.service'

const OTP_DIGIT_COUNT = TWOFACTOR_RATE_LIMITS.OTP_DIGIT_COUNT

// Interface for OtpInput component methods
interface OtpInputComponent {
    focus(): void
}

@Component
export class TwoFactorLockoutMixin extends Vue {
    // OTP state
    otpDigits: string[] = createEmptyOtpDigits()
    codeError = ''
    codeErrorActive = false

    // Attempt tracking
    attemptsRemaining: number = TWOFACTOR_RATE_LIMITS.VERIFY_MAX_ATTEMPTS
    maxAttempts: number = TWOFACTOR_RATE_LIMITS.VERIFY_MAX_ATTEMPTS

    // Lockout state (from wrong OTP attempts)
    isLockedOut = false
    lockoutCountdown = 0
    lockoutDuration: number = TWOFACTOR_RATE_LIMITS.VERIFY_LOCKOUT_SECONDS

    // Rate limit state (from too many email sends)
    rateLimited = false
    rateLimitMessage = ''
    rateLimitCountdown = 0
    rateLimitDuration = 0

    // Interval handles
    lockoutInterval: number | null = null
    countdownInterval: number | null = null

    // Flag to suppress focus-triggered error clearing during programmatic reset
    suppressFocusClear = false

    // Abstract property - must be overridden by implementing class
    // This returns the storage instance for the specific page
    get lockoutStorage(): TwoFactorStorage {
        throw new Error('lockoutStorage getter must be overridden')
    }

    // Computed properties
    get otpComplete(): boolean {
        return this.otpDigits.every(digit => digit !== '') && this.otpDigits.length === OTP_DIGIT_COUNT
    }

    get confirmationCode(): string {
        return this.otpDigits.join('')
    }

    get attemptsClass(): string {
        if (this.attemptsRemaining === 1) return 'danger'
        if (this.attemptsRemaining === 2) return 'warning'
        return ''
    }

    // Methods
    clearCodeErrorActive(): void {
        if (this.suppressFocusClear) return
        this.codeErrorActive = false
    }

    /**
     * Start the lockout countdown interval
     * This is extracted so it can be called both when starting a fresh lockout
     * and when restoring lockout state from storage
     */
    private startLockoutInterval(): void {
        if (this.lockoutInterval) {
            clearInterval(this.lockoutInterval)
        }

        // If countdown is already expired, end lockout immediately
        if (this.lockoutCountdown <= 0) {
            this.endLockout()
            return
        }

        this.lockoutInterval = window.setInterval(() => {
            this.lockoutCountdown--
            if (this.lockoutCountdown <= 0) {
                this.endLockout()
            }
        }, 1000)
    }

    /**
     * Start a lockout period after exhausting OTP attempts
     */
    startLockout(): void {
        this.isLockedOut = true
        this.lockoutCountdown = this.lockoutDuration
        this.lockoutStorage.saveLockout(this.lockoutDuration)

        this.startLockoutInterval()
    }

    /**
     * Reset lockout state and return whether we were locked out.
     * Use this in overridden endLockout() methods to avoid duplicating reset logic.
     * @returns true if we were locked out before the reset
     */
    protected resetLockoutState(): boolean {
        const wasLockedOut = this.isLockedOut

        this.isLockedOut = false
        this.attemptsRemaining = this.maxAttempts
        this.codeError = ''
        this.codeErrorActive = false
        this.lockoutStorage.removeLockout()

        if (this.lockoutInterval) {
            clearInterval(this.lockoutInterval)
            this.lockoutInterval = null
        }

        return wasLockedOut
    }

    /**
     * End the lockout period and reset state
     * Override this in implementing class if additional cleanup is needed
     */
    endLockout(): void {
        this.resetLockoutState()
    }

    /**
     * Start the rate limit countdown interval
     * This is extracted so it can be called both when setting a fresh rate limit
     * and when restoring rate limit state from storage
     */
    private startRateLimitInterval(): void {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval)
        }

        // If countdown is already expired, handle expiry immediately
        if (this.rateLimitCountdown <= 0) {
            this.onRateLimitExpired()
            return
        }

        this.countdownInterval = window.setInterval(() => {
            this.rateLimitCountdown--
            if (this.rateLimitCountdown <= 0) {
                this.onRateLimitExpired()
            }
        }, 1000)
    }

    /**
     * Set rate limit state (typically from email send rate limiting)
     */
    setRateLimit(minutes: number, message: string): void {
        this.rateLimited = true
        this.rateLimitMessage = message
        const totalSeconds = minutes * 60
        this.rateLimitDuration = totalSeconds
        this.rateLimitCountdown = totalSeconds

        this.lockoutStorage.saveRateLimit(minutes, message)

        this.startRateLimitInterval()
    }

    /**
     * Called when rate limit expires
     * Override this in implementing class if additional cleanup is needed
     */
    onRateLimitExpired(): void {
        this.rateLimited = false
        this.rateLimitMessage = ''
        this.attemptsRemaining = this.maxAttempts
        this.lockoutStorage.removeRateLimit()

        if (this.countdownInterval) {
            clearInterval(this.countdownInterval)
            this.countdownInterval = null
        }

        // Reload page to trigger fresh state
        window.location.reload()
    }

    /**
     * Reset OTP digits to empty state.
     * Note: This intentionally does NOT call otpInput.reset() because that method
     * focuses the first input, which triggers the @focus event and clears error states.
     * Pages that need focus after reset should call focusOtpInput() separately.
     */
    resetOtpDigits(): void {
        this.otpDigits = createEmptyOtpDigits()
    }

    /**
     * Focus the first OTP input field
     */
    focusOtpInput(): void {
        const otpInput = this.$refs.otpInput as OtpInputComponent | undefined
        if (otpInput) {
            otpInput.focus()
        }
    }

    /**
     * Restore lockout state from sessionStorage
     */
    restoreLockoutState(): void {
        const lockoutState = this.lockoutStorage.getLockout()
        if (lockoutState) {
            this.isLockedOut = true
            this.lockoutCountdown = lockoutState.remainingSeconds
            this.lockoutDuration = lockoutState.duration
            // Start the interval to continue counting down
            this.startLockoutInterval()
        }

        const rateLimitState = this.lockoutStorage.getRateLimit()
        if (rateLimitState) {
            this.rateLimited = true
            this.rateLimitMessage = rateLimitState.message
            this.rateLimitDuration = rateLimitState.duration
            this.rateLimitCountdown = rateLimitState.remainingSeconds
            // Start the interval to continue counting down
            this.startRateLimitInterval()
        }
    }

    /**
     * Check and restore lockout state from sessionStorage or database
     * @param command - Optional command to fetch auth status from database
     */
    async checkAndRestoreLockoutState(command?: TwoFactorAuthStatusCommand): Promise<void> {
        const result = await checkLockoutState(this.lockoutStorage, command)

        if (!result) return

        // If from session, use the existing restoreLockoutState method
        if (result.fromSession) {
            this.restoreLockoutState()
            return
        }

        // Apply verify lockout state from database
        if (result.isLockedOut) {
            this.isLockedOut = true
            this.lockoutCountdown = result.lockoutCountdown
            this.lockoutDuration = result.lockoutDuration
            // Start the interval to continue counting down
            this.startLockoutInterval()
        }

        // Apply email rate limit state from database
        if (result.rateLimited) {
            this.rateLimited = true
            this.rateLimitDuration = result.rateLimitDuration
            this.rateLimitCountdown = result.rateLimitCountdown
            this.rateLimitMessage = result.rateLimitMessage
            // Start the interval to continue counting down
            this.startRateLimitInterval()
        }
    }

    /**
     * Handle an incorrect OTP code submission
     * @param onLockout - Optional callback when lockout starts
     */
    handleIncorrectCode(onLockout?: () => void): void {
        this.attemptsRemaining--
        this.codeErrorActive = true

        if (this.attemptsRemaining <= 0) {
            this.codeError = this.$t('page.twofactor.error.no_attempts') as string
            // Start lockout after a short delay to show the error first
            setTimeout(() => {
                this.startLockout()
                onLockout?.()
            }, 1500)
        } else if (this.attemptsRemaining === 1) {
            this.codeError = this.$t('page.twofactor.error.incorrect_code_last') as string
        } else {
            this.codeError = this.$t('page.twofactor.error.incorrect_code', {
                count: this.attemptsRemaining
            }) as string
        }

        // Clear the OTP inputs after a short delay and focus first input box
        setTimeout(() => {
            this.suppressFocusClear = true
            this.resetOtpDigits()
            this.$nextTick(() => {
                this.focusOtpInput()
                this.$nextTick(() => {
                    this.suppressFocusClear = false
                })
            })
        }, 1000)
    }

    /**
     * Cleanup intervals on component destroy
     */
    cleanupLockoutIntervals(): void {
        if (this.lockoutInterval) {
            clearInterval(this.lockoutInterval)
            this.lockoutInterval = null
        }
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval)
            this.countdownInterval = null
        }
    }

    beforeDestroy(): void {
        this.cleanupLockoutIntervals()
    }
}
